import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/admin';

// Thunks

export const getAllCompanies = createAsyncThunk(
  'company/getAllCompanies',
  async () => {
    const data = await api.getAllCompanies();
    return data;
  },
);

export const getCompanyByReference = createAsyncThunk(
  'company/getCompanyByReference',
  async ({ reference }) => {
    const data = await api.getCompanyByReference(reference);
    return data;
  },
);

export const addCompany = createAsyncThunk(
  'company/addCompany',
  async (company, { rejectWithValue }) => {
    try {
      const data = await api.addCompany(company);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateCompany = createAsyncThunk(
  'company/updateCompany',
  async (company, { rejectWithValue }) => {
    try {
      const data = await api.updateCompany(company);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteCompany = createAsyncThunk(
  'company/deleteCompany',
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const data = await api.deleteCompany(companyId);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getReservationTypes = createAsyncThunk(
  'company/getReservationTypes',
  async ({ clientId }, { rejectWithValue }) => {
    try {
      const data = await api.getReservationTypes(clientId);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getEnabledReservationTypes = createAsyncThunk(
  'company/getEnabledReservationTypes',
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const data = await api.getEnabledReservationTypes(companyId);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const setEnabledReservationTypes = createAsyncThunk(
  'company/setEnabledReservationTypes',
  async ({ companyId, selectedReservationTypes }, { rejectWithValue }) => {
    try {
      const data = await api.setEnabledReservationTypes(companyId, selectedReservationTypes);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getProperties = createAsyncThunk(
  'company/getProperties',
  async ({ clientId }, { rejectWithValue }) => {
    try {
      const data = await api.getProperties(clientId);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getEnabledProperties = createAsyncThunk(
  'company/getEnabledProperties',
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const data = await api.getEnabledProperties(companyId);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const setEnabledProperties = createAsyncThunk(
  'company/setEnabledProperties',
  async ({ companyId, selectedProperties }, { rejectWithValue }) => {
    try {
      const data = await api.setEnabledProperties(companyId, selectedProperties);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Slice

export const companySlice = createSlice({
  name: 'company',
  initialState: {
    companies: [],
    hasUnsavedChanges: false,
    isPending: false,
    editedIndexes: {},
    currentCompany: null,
  },
  reducers: {
    updateCompanies: (state, action) => {
      state.companies = action.payload;
    },
    setHasUnsavedChanges: (state, action) => {
      state.hasUnsavedChanges = action.payload;
    },
    setEditedIndexes: (state, action) => {
      const { id, indexes } = action.payload;
      state.editedIndexes[id] = indexes;
    },
    setCurrentCompany: (state, action) => {
      state.currentCompany = action.payload;
    },
  },
  extraReducers: {
    [getAllCompanies.rejected]: (state) => {
      state.isPending = false;
    },
    [getAllCompanies.pending]: (state) => {
      state.isPending = true;
    },
    [getAllCompanies.fulfilled]: (state, action) => {
      state.companies = action.payload;
      state.isPending = false;
    },
    [getCompanyByReference.rejected]: (state) => {
      state.isPending = false;
    },
    [getCompanyByReference.pending]: (state) => {
      state.isPending = true;
    },
    [getCompanyByReference.fulfilled]: (state, action) => {
      state.currentCompany = action.payload;
      state.isPending = false;
    },
    [addCompany.rejected]: (state) => {
      state.isPending = false;
    },
    [addCompany.pending]: (state) => {
      state.isPending = true;
    },
    [addCompany.fulfilled]: (state, action) => {
      state.companies = [...state.companies, action.payload];
      state.isPending = false;
    },
    [updateCompany.rejected]: (state) => {
      state.isPending = false;
    },
    [updateCompany.pending]: (state) => {
      state.isPending = true;
    },
    [updateCompany.fulfilled]: (state, action) => {
      state.companies = [...state.companies, action.payload];
      state.isPending = false;
    },
    [deleteCompany.rejected]: (state) => {
      state.isPending = false;
    },
    [deleteCompany.pending]: (state) => {
      state.isPending = true;
    },
    [deleteCompany.fulfilled]: (state, action) => {
      state.companies.splice(state.companies.findIndex((i) => i.id === action.payload.deletedId), 1);
      state.isPending = false;
    },
    [getReservationTypes.rejected]: (state) => {
      state.isPending = false;
    },
    [getReservationTypes.pending]: (state) => {
      state.isPending = true;
    },
    [getReservationTypes.fulfilled]: (state) => {
      state.isPending = false;
    },
    [getEnabledReservationTypes.rejected]: (state) => {
      state.isPending = false;
    },
    [getEnabledReservationTypes.pending]: (state) => {
      state.isPending = true;
    },
    [getEnabledReservationTypes.fulfilled]: (state) => {
      state.isPending = false;
    },
    [setEnabledReservationTypes.rejected]: (state) => {
      state.isPending = false;
    },
    [setEnabledReservationTypes.pending]: (state) => {
      state.isPending = true;
    },
    [setEnabledReservationTypes.fulfilled]: (state) => {
      state.isPending = false;
    },
    [getProperties.rejected]: (state) => {
      state.isPending = false;
    },
    [getProperties.pending]: (state) => {
      state.isPending = true;
    },
    [getProperties.fulfilled]: (state) => {
      state.isPending = false;
    },
    [getEnabledProperties.rejected]: (state) => {
      state.isPending = false;
    },
    [getEnabledProperties.pending]: (state) => {
      state.isPending = true;
    },
    [getEnabledProperties.fulfilled]: (state) => {
      state.isPending = false;
    },
    [setEnabledProperties.rejected]: (state) => {
      state.isPending = false;
    },
    [setEnabledProperties.pending]: (state) => {
      state.isPending = true;
    },
    [setEnabledProperties.fulfilled]: (state) => {
      state.isPending = false;
    },
  },
});

// Actions

export const {
  updateCompanies,
  setHasUnsavedChanges,
  setEditedIndexes,
  setCurrentCompany,
} = companySlice.actions;

// Selectors

export const selectCompanies = (state) => state.company.companies;
export const selectIsPending = (state) => state.company.isPending;
export const selectNoCompanyFound = (state) => state.company.noCompanyFound;
export const selectHasUnsavedChanges = (state) => state.company.hasUnsavedChanges;
export const selectEditedIndexes = (state) => state.company.editedIndexes;
export const selectCurrentCompany = (state) => state.company.currentCompany;

// Reducer

export default companySlice.reducer;
